import { cn } from "@/lib/utils";

export function Panel({
  children,
  className,
  oneview,
}: {
  children: React.ReactNode;
  className?: string;
  oneview?: boolean;
}) {
  return (
    <div
      className={cn(
        "rounded-[10px] border bg-card shadow-[0_1px_2px_rgba(22,35,46,.08),0_4px_16px_rgba(22,35,46,.06)]",
        oneview ? "border-[#dc2626] ring-1 ring-[#dc2626]/50" : "border-border",
        className,
      )}
    >
      {children}
    </div>
  );
}

export function PanelHead({
  title,
  children,
}: {
  title: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-2.5 border-b border-[#e6ebf0] px-4 py-3">
      <h3 className="text-[13.5px] font-semibold">{title}</h3>
      {children}
    </div>
  );
}

export function Chip({
  tone = "neutral",
  children,
}: {
  tone?: "ok" | "warn" | "alert" | "neutral" | "info";
  children: React.ReactNode;
}) {
  const tones = {
    ok: "bg-ok-wash text-ok",
    warn: "bg-warn-wash text-warn",
    alert: "bg-alert-wash text-alert",
    info: "bg-accent text-primary",
    neutral: "border border-[#e6ebf0] bg-secondary text-[#43535f]",
  };
  return (
    <span className={cn("inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold", tones[tone])}>
      {children}
    </span>
  );
}

export function Pill({
  tone = "neutral",
  children,
}: {
  tone?: "ok" | "warn" | "alert" | "info" | "neutral";
  children: React.ReactNode;
}) {
  return <Chip tone={tone}>{children}</Chip>;
}

export function Kv({
  items,
  wide,
}: {
  items: { label: string; value: React.ReactNode; mark?: React.ReactNode }[];
  wide?: boolean;
}) {
  return (
    <dl className={cn("grid gap-y-1.5 text-[13px]", wide ? "grid-cols-[190px_1fr]" : "grid-cols-[150px_1fr]")}>
      {items.map((item) => (
        <div key={item.label} className="contents">
          <dt className="flex items-center gap-1.5 text-muted-foreground">
            {item.label}
            {item.mark}
          </dt>
          <dd className="font-medium">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function StatusTone(status: string): "ok" | "warn" | "alert" | "info" | "neutral" {
  const s = status.toLowerCase();
  if (s.includes("degraded") || s.includes("elevated") || s.includes("locked")) return "warn";
  if (s.includes("alert") || s.includes("p1")) return "alert";
  if (s.includes("progress") || s.includes("suggested") || s.includes("info")) return "info";
  if (s.includes("active") || s.includes("signed") || s.includes("good") || s.includes("resolved")) return "ok";
  return "neutral";
}
